import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  enqueueMailcomRenewal,
  postMailcomClearCooldown,
  postMailcomForceRenew,
  postProviderParentReactivate,
  refreshMailcomSession,
  revealMailcomCredential,
  type WireProviderAccount,
} from '../../api/endpoints.js';
import { useApiMutation } from '../../api/hooks.js';
import {
  loginFailureLabel,
  loginFailureTitle,
  type LoginFailureReasonWire,
} from '../../i18n/login-failure-reasons.js';
import {
  Badge,
  Button,
  Icon,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TRow,
} from '../../ui/index.js';
import {
  ACCOUNT_STATUS_LABEL,
  fmtDate,
  fmtDateOnly,
  STATUS_DETAIL_LABEL,
  SUSPENDED_REASON_LABEL,
  toneForAccountStatus,
  toneForStatusDetail,
} from './shared.js';
import {
  reactivateKindOf,
  supportsOperatorOffSale,
  type ReactivateKind,
} from './parentSaleApi.js';
import { TakeOffSaleDialog } from './TakeOffSaleDialog.js';

export interface ParentMailsTableProps {
  readonly providerId: string;
  readonly accounts: readonly WireProviderAccount[];
  readonly canManage: boolean;
  readonly onOpenAliases: (accountId: string) => void;
  readonly onOpenRenewals: (accountId: string) => void;
  readonly onOpenCredential: (accountId: string) => void;
  readonly onAfterAction: () => void;
  /**
   * 2026-09-23 (D-239) — row selection for the bulk "Satıştan çek" bar; the
   * state lives in AccountsGrid. The checkbox column renders only when BOTH are
   * given, the caller can manage, and the server serves the provider's
   * take-off-sale (supportsOperatorOffSale — gmx today). Disabled rows are not
   * selectable (the server would skip them).
   */
  readonly selectedIds?: ReadonlySet<string>;
  readonly onSelectionChange?: (next: ReadonlySet<string>) => void;
}

/** Per-row checkbox wiring handed down by ParentMailsTable. */
interface RowSelection {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
}

/**
 * V1-style parent mail table. Replaces the card grid in AccountsGrid for
 * an information-dense operator view. Below 1280px (phones, tablets and
 * small laptops with the sidebar, where the 10-column table cannot fit) we
 * swap to a stacked card list — matches V1's md:hidden fallback
 * (ParentMails.jsx:682-720); the card list keeps the select-all checkbox.
 * From 1280px the table fits its card; if it ever cannot, it scrolls
 * sideways inside its own container (never the page) with the ⚙ column
 * pinned to the right edge.
 *
 * 2026-05-28 update — operatör isteği: "Oturum" kolonu kaldırıldı (V1'de
 * yok) ve per-row aksiyon butonları tek bir ⚙ menüsü altında toplandı
 * (desktop + mobile aynı menüyü kullanır).
 */
export function ParentMailsTable({
  providerId,
  accounts,
  canManage,
  onOpenAliases,
  onOpenRenewals,
  onOpenCredential,
  onAfterAction,
  selectedIds,
  onSelectionChange,
}: ParentMailsTableProps): JSX.Element {
  const isMobile = useIsMobile();

  // 2026-09-23 (D-239) — bulk selection (alias-model providers only).
  const selectionEnabled =
    canManage &&
    supportsOperatorOffSale(providerId) &&
    selectedIds !== undefined &&
    onSelectionChange !== undefined;
  const selectableIds = accounts.filter((a) => a.status !== 'disabled').map((a) => a.id);
  const selectedOnPage = selectableIds.filter((id) => selectedIds?.has(id)).length;
  const allSelected = selectableIds.length > 0 && selectedOnPage === selectableIds.length;
  const selectionFor = (a: WireProviderAccount): RowSelection | undefined => {
    if (!selectionEnabled) return undefined;
    const disabled = a.status === 'disabled';
    return {
      checked: !disabled && Boolean(selectedIds?.has(a.id)),
      disabled,
      onToggle: () => {
        const next = new Set(selectedIds);
        if (next.has(a.id)) next.delete(a.id);
        else next.add(a.id);
        onSelectionChange?.(next);
      },
    };
  };
  const toggleAll = (): void => {
    const next = new Set(selectedIds);
    if (allSelected) for (const id of selectableIds) next.delete(id);
    else for (const id of selectableIds) next.add(id);
    onSelectionChange?.(next);
  };

  // The same select-all input as the table header (only one of table / cards
  // is ever mounted), so the card list keeps "select the whole page".
  const selectAll = selectionEnabled ? (
    <input
      type="checkbox"
      aria-label="Sayfadaki seçilebilir parent'ların hepsini seç"
      checked={allSelected}
      disabled={selectableIds.length === 0}
      ref={(el) => {
        if (el) el.indeterminate = selectedOnPage > 0 && !allSelected;
      }}
      onChange={toggleAll}
      data-testid="parents-select-all"
    />
  ) : null;

  if (isMobile) {
    return (
      <>
        {selectAll ? (
          // Card-list header strip (the table's checkbox header has no row here).
          <label className="mcp-cards-selectall">
            <span className="mcp-card-check">{selectAll}</span>
            <span aria-hidden="true">Sayfadaki seçilebilir parent&apos;ların hepsini seç</span>
          </label>
        ) : null}
        <div className="mcp-cards" data-testid="mailcom-parents-mobile-list">
          {accounts.map((a) => (
            <ParentMailMobileCard
              key={a.id}
              providerId={providerId}
              account={a}
              canManage={canManage}
              onOpenAliases={onOpenAliases}
              onOpenRenewals={onOpenRenewals}
              onOpenCredential={onOpenCredential}
              onAfterAction={onAfterAction}
              selection={selectionFor(a)}
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <div className="tbl-scroll mcp-table-wrap" data-testid="mailcom-parents-table-wrap">
      <Table dense className="mcp-table">
        <THead>
          <TRow>
            {selectAll ? <TH style={{ width: 28 }}>{selectAll}</TH> : null}
            <TH>Email</TH>
            <TH>Durum</TH>
            <TH>Cooldown</TH>
            <TH style={{ textAlign: 'center' }}>Cred</TH>
            <TH>Şifre</TH>
            <TH style={{ textAlign: 'right' }}>Hata</TH>
            <TH>Son aktivite</TH>
            <TH>Kayıt</TH>
            <TH className="mcp-td-actions" style={{ textAlign: 'right' }}>İşlemler</TH>
          </TRow>
        </THead>
        <TBody>
          {accounts.map((a) => (
            <ParentMailRow
              key={a.id}
              providerId={providerId}
              account={a}
              canManage={canManage}
              onOpenAliases={onOpenAliases}
              onOpenRenewals={onOpenRenewals}
              onOpenCredential={onOpenCredential}
              onAfterAction={onAfterAction}
              selection={selectionFor(a)}
            />
          ))}
        </TBody>
      </Table>
    </div>
  );
}

/** 2026-09-23 (D-239) — the row / card checkbox for the bulk "Satıştan çek" bar. */
function SelectBox({
  selection,
  handle,
}: {
  readonly selection: RowSelection;
  readonly handle: string;
}): JSX.Element {
  return (
    <input
      type="checkbox"
      aria-label={`${handle} seç`}
      title={selection.disabled ? 'Devre dışı parent seçilemez' : undefined}
      checked={selection.checked}
      disabled={selection.disabled}
      onChange={selection.onToggle}
      data-testid={`parent-select-${handle}`}
    />
  );
}

/**
 * The handle with soft line-break opportunities before the domain and after
 * each ".", "-" and "_" of the local part, so a narrow cell / card wraps it
 * as "local-part" + "@domain", and a local part longer than the line at a
 * word boundary ("operations.parent.mailbox." / "team-2026.…"), instead of
 * at an arbitrary character. The browser only uses them when the handle does
 * not fit; the text content (copy, search, tests) is unchanged: the parts
 * stay sibling text nodes of the caller's element.
 */
function HandleText({ handle }: { readonly handle: string }): JSX.Element {
  const at = handle.lastIndexOf('@');
  if (at <= 0) return <>{handle}</>;
  const local = handle.slice(0, at).split(/(?<=[.\-_])/).filter((p) => p !== '');
  return (
    <>
      {local.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {part}
        </Fragment>
      ))}
      <wbr />
      {handle.slice(at)}
    </>
  );
}

/** "26.05.26 04:19" — yıl 2 hane + saniye yok, kompakt göster. */
function fmtCompact(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mn = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yy} ${hh}:${mn}`;
  } catch {
    return iso;
  }
}

/** İki timestamp'ten en yenisini döndür (her ikisi null ise null). */
function newerOf(a: string | null | undefined, b: string | null | undefined): string | null {
  if (!a && !b) return null;
  if (!a) return b ?? null;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
}

/* ─── R-D214 — V1-parity status badges + cooldown countdown chip ── */

/**
 * Stacked PRIMARY (status bucket) + SECONDARY (V1 taxonomy token) badge
 * pair. The secondary badge renders only when `status_detail` is set and
 * different from the primary status (so we don't show 'aktif / Aktif').
 * Unknown tokens fall back to a neutral pill with the raw token (matches
 * the V1 ParentMails.jsx fallback pattern explicitly mitigated here).
 */
function StatusBadgePair({
  status,
  statusDetail,
  suspendedReason,
  failureReason,
  handle,
  layout = 'stack',
}: {
  readonly status: string;
  readonly statusDetail?: string | null;
  readonly suspendedReason?: string | null;
  /** 2026-09-22 — shown under the status for login_failed rows only. */
  readonly failureReason?: LoginFailureReasonWire | null;
  readonly handle: string;
  /** 'stack' (table cell) = one badge per line; 'row' (card) = badges flow and wrap. */
  readonly layout?: 'stack' | 'row';
}): JSX.Element {
  // Pick the meaningful secondary token. Prefer suspended_reason for
  // cooldown-pool rows (its label carries the V1 cooldown semantics);
  // otherwise fall back to status_detail when present and distinct from
  // status.
  const secondaryToken =
    status === 'suspended' && suspendedReason
      ? suspendedReason
      : statusDetail && statusDetail !== status
        ? statusDetail
        : null;
  return (
    <div
      className="mcp-badges"
      style={
        layout === 'row'
          ? { display: 'flex', flexWrap: 'wrap', gap: '4px 6px', alignItems: 'center', minWidth: 0 }
          : { display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }
      }
    >
      <Badge tone={toneForAccountStatus(status)} dot>
        {ACCOUNT_STATUS_LABEL[status] ?? status}
      </Badge>
      {secondaryToken ? (
        <Badge tone={toneForStatusDetail(secondaryToken)}>
          {STATUS_DETAIL_LABEL[secondaryToken]
            ?? SUSPENDED_REASON_LABEL[secondaryToken]
            ?? secondaryToken}
        </Badge>
      ) : null}
      <FailureReasonBadge status={status} reason={failureReason} handle={handle} />
    </div>
  );
}

/**
 * 2026-09-22 — WHY a parent is login_failed: the server-classified reason
 * code mapped to a Turkish label, with the hint + cleaned server reply as the
 * tooltip. Rendered only for status='login_failed' (the stored reason is stale
 * history on any other row). Long labels wrap instead of widening the column.
 */
function FailureReasonBadge({
  status,
  reason,
  handle,
  detail = false,
}: {
  readonly status: string;
  readonly reason?: LoginFailureReasonWire | null;
  readonly handle: string;
  /** Card only: also render the tooltip text as a touch-screen line (.touch-detail). */
  readonly detail?: boolean;
}): JSX.Element | null {
  if (status !== 'login_failed' || !reason) return null;
  const tooltip = [loginFailureTitle(reason), reason.at ? `Son ret: ${fmtDate(reason.at)}` : '']
    .filter(Boolean)
    .join(' — ');
  const badge = (
    <Badge
      tone="danger"
      title={tooltip || undefined}
      data-testid={`failure-reason-${handle}`}
      style={{
        maxWidth: 260,
        height: 'auto',
        minHeight: 22,
        padding: '2px 8px',
        whiteSpace: 'normal',
        lineHeight: 1.35,
        textAlign: 'left',
      }}
    >
      {loginFailureLabel(reason.code).label}
    </Badge>
  );
  if (!detail || !tooltip) return badge;
  // A touch screen cannot open the tooltip (hint, server reply, last
  // rejection): the same text under the badge, touch screens only.
  return (
    <>
      {badge}
      <div className="touch-detail mcp-card-reason-detail">{tooltip}</div>
    </>
  );
}

/**
 * Live countdown chip — one local 1-s ticker per row. When
 * `remainingSec <= 0` the chip flips to a green "Yeniden Uygun" pill until
 * the next data refresh removes it.
 *
 * 2026-06-10 flicker fix (RC4): the old effect was keyed on
 * [cooldownUntil, initialRemainingSec] — and the server recomputes
 * cooldown_remaining_sec from NOW() on EVERY poll response, so each settle
 * tore the interval down and reset the visible counter phase (~every 10s
 * stutter). The ticker is now keyed on the cooldown WINDOW only; the
 * server's remaining is adopted solely when it genuinely disagrees.
 */
function CooldownChip({
  cooldownUntil,
  initialRemainingSec,
  suspendedReason,
}: {
  readonly cooldownUntil: string;
  readonly initialRemainingSec: number;
  readonly suspendedReason?: string | null;
}): JSX.Element {
  const [remaining, setRemaining] = useState(initialRemainingSec);
  // Refs so the resync effect can read the live countdown without taking
  // `remaining` as a dependency (that would re-run it every second).
  const remainingRef = useRef(remaining);
  remainingRef.current = remaining;
  const lastUntilRef = useRef(cooldownUntil);

  // Local 1-s decrement, keyed on the cooldown window only — poll settles
  // never recreate the interval, so the counter cadence stays smooth.
  useEffect(() => {
    const id = window.setInterval(() => {
      setRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  // Server resync guard: hard resync when the cooldown window itself changed
  // (operator clear / fresh suspend); otherwise adopt the server's NOW()-based
  // remaining only when it drifts >2s from the local countdown (e.g. hidden-
  // tab timer throttling). ≤2s poll jitter never resets the visible phase.
  useEffect(() => {
    if (lastUntilRef.current !== cooldownUntil) {
      lastUntilRef.current = cooldownUntil;
      setRemaining(initialRemainingSec);
      return;
    }
    if (Math.abs(remainingRef.current - initialRemainingSec) > 2) {
      setRemaining(initialRemainingSec);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooldownUntil, initialRemainingSec]);

  const tooltipLabel =
    (suspendedReason && SUSPENDED_REASON_LABEL[suspendedReason])
      || suspendedReason
      || 'Cooldown';
  if (remaining <= 0) {
    return (
      <Badge tone="success" title={`Cooldown bitti · ${cooldownUntil}`}>
        Yeniden Uygun
      </Badge>
    );
  }
  const hh = String(Math.floor(remaining / 3600)).padStart(2, '0');
  const mm = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  return (
    <Badge tone="warn" title={`${tooltipLabel} · ${cooldownUntil}`}>
      Tekrar deneme: {hh}:{mm}:{ss}
    </Badge>
  );
}

/**
 * Card list below 1280px (phones, tablets, small laptops with the sidebar):
 * the 10-column table needs ~920px of card width, so below that it showed
 * only part of its columns (the ⚙ actions included).
 */
const CARD_LIST_QUERY = '(max-width: 1279px)';

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(CARD_LIST_QUERY).matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(CARD_LIST_QUERY);
    const handler = (e: MediaQueryListEvent): void => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

/* ─── shared per-row logic ─────────────────────────────────────── */

interface RowActions {
  readonly renewBlocked: boolean;
  /**
   * 2026-09-22 — row is disabled at the GMX phone wall (status='disabled' +
   * status_detail='phone_verification_required'). Force Renew is blocked
   * (server 409 phone_verification_disabled) and "Yeniden etkinleştir" shows.
   */
  readonly phoneWall: boolean;
  /**
   * 2026-09-23 (D-239) — which "reactivate-only" disabled kind the row is in
   * (phone wall, operator take-off-sale, IMAP quarantine), else null. Force
   * Renew is blocked for all three (gmx server: 409) and "Yeniden
   * etkinleştir" shows.
   */
  readonly reactivateKind: ReactivateKind | null;
  /** "Satıştan çek" is offered (alias-model provider + row not disabled). */
  readonly canTakeOffSale: boolean;
  /** "Yeniden etkinleştir" is offered (the reactivate route serves this kind here). */
  readonly canReactivate: boolean;
  readonly sessionRefreshLoading: boolean;
  readonly renewNowLoading: boolean;
  readonly forceRenewLoading: boolean;
  readonly clearCooldownLoading: boolean;
  readonly reactivateLoading: boolean;
  readonly cooldownActive: boolean;
  /**
   * Only mail.com has a clear-cooldown route
   * (/admin/providers/mailcom/parents/:id/clear-cooldown); there is no generic
   * one, so on other providers the item would always 404. The chip still shows.
   */
  readonly clearCooldownSupported: boolean;
  /**
   * 2026-09-22 — "Oturum yenile" only has a mail.com route
   * (/admin/providers/mailcom/accounts/:id/session-refresh); on gmx it answered
   * 404, so the item is hidden for every other provider.
   */
  readonly sessionRefreshSupported: boolean;
  readonly anyError: boolean;
  readonly errorText: string | null;
  readonly onRefreshSession: () => Promise<void>;
  readonly onRenewNow: () => Promise<void>;
  readonly onForceRenew: () => Promise<void>;
  readonly onClearCooldown: () => Promise<void>;
  readonly onReactivate: () => Promise<void>;
}

/**
 * 2026-09-23 (D-239) — what "Yeniden etkinleştir" does on mail.com: the parent
 * becomes active with session_state 'expired'; its remaining aliases sell at
 * once, and nothing re-verifies the session automatically (the renewal
 * scheduler needs 'active'), hence the pointer to "Oturum yenile".
 */
const MAILCOM_REACTIVATE_EFFECT =
  'Hesap aktif olur ve kalan aliasları hemen yeniden satışa girer. mail.com’da oturum kendiliğinden '
  + 'doğrulanmaz ve otomatik yenileme başlamaz; gerekirse satırdaki “Oturum yenile”yi kullanın.';

function useRowActions(providerId: string, account: WireProviderAccount, onAfterAction: () => void): RowActions {
  const sessionRefresh = useApiMutation((o, accountId: string) =>
    refreshMailcomSession(o, accountId, providerId),
  );
  const renewNow = useApiMutation((o, accountId: string) =>
    enqueueMailcomRenewal(o, accountId, null, providerId),
  );
  const forceRenew = useApiMutation((o, input: { reason: string }) =>
    postMailcomForceRenew(o, account.id, {
      reason: input.reason,
      confirmationToken: account.id,
    }, providerId),
  );
  const clearCooldown = useApiMutation((o, input: { reason: string }) =>
    postMailcomClearCooldown(o, account.id, { reason: input.reason }, providerId),
  );
  const reactivate = useApiMutation((o, input: { reason: string }) =>
    postProviderParentReactivate(o, providerId, account.id, input.reason),
  );

  // 2026-09-22 — 'disabled' joins the blocked set: the control plane's
  // enqueueRenewal rejects a disabled parent, so the item is greyed instead of
  // erroring.
  const renewBlocked =
    account.status === 'login_failed' || account.status === 'suspended' || account.status === 'disabled';
  const phoneWall =
    account.status === 'disabled' && account.statusDetail === 'phone_verification_required';
  const reactivateKind = reactivateKindOf(account);
  const offSaleSupported = supportsOperatorOffSale(providerId);
  const canTakeOffSale = offSaleSupported && account.status !== 'disabled';
  // The phone-wall inverse existed before D-239 on every mount; the new kinds
  // are served for the alias-model providers only.
  const canReactivate =
    reactivateKind === 'phone_wall' || (reactivateKind !== null && offSaleSupported);
  const cooldownActive = Boolean(
    account.cooldownUntil
      && account.cooldownRemainingSec !== null
      && account.cooldownRemainingSec !== undefined
      && account.cooldownRemainingSec > 0,
  );

  async function onRefreshSession(): Promise<void> {
    const r = await sessionRefresh.run(account.id);
    if (r.kind === 'ok') onAfterAction();
  }

  async function onRenewNow(): Promise<void> {
    const r = await renewNow.run(account.id);
    if (r.kind === 'ok') onAfterAction();
  }

  async function onForceRenew(): Promise<void> {
    if (
      !window.confirm(
        `FORCE Renew: ${account.accountHandle}. G2 active-order guard bypass EDİLMEZ. Devam?`,
      )
    ) {
      return;
    }
    const reason = window.prompt("Neden? (≥4 karakter, audit log'a yazılır)");
    if (!reason || reason.trim().length < 4) {
      window.alert('Neden en az 4 karakter olmalı. İptal.');
      return;
    }
    const r = await forceRenew.run({ reason: reason.trim() });
    if (r.kind === 'ok') onAfterAction();
  }

  async function onClearCooldown(): Promise<void> {
    // R-D214 — operator override: zero out cooldown_until + flip to active.
    if (!window.confirm(`Cooldown sıfırla: ${account.accountHandle}. Parent hemen yeniden uygun hale gelecek. Devam?`)) {
      return;
    }
    const reason = window.prompt("Neden? (≥4 karakter, audit log'a yazılır)");
    if (!reason || reason.trim().length < 4) {
      window.alert('Neden en az 4 karakter olmalı. İptal.');
      return;
    }
    const r = await clearCooldown.run({ reason: reason.trim() });
    if (r.kind === 'ok') onAfterAction();
  }

  async function onReactivate(): Promise<void> {
    // 2026-09-22 — operator override for a phone-wall-disabled GMX parent: the
    // server flips it back to active and returns its 'disabled' aliases to
    // sale. Nothing is enqueued; the scheduler picks it up on its own.
    // 2026-09-23 (D-239) — the same route reactivates an operator take-off-sale
    // or an IMAP quarantine: active + session_state 'expired'; nothing enqueued.
    // mail.com: nothing re-verifies an 'expired' session on its own (the renewal
    // scheduler needs session_state 'active', the bootstrap sweeper only picks
    // 'unknown') while the remaining aliases sell at once, so the copy says so
    // and points to "Oturum yenile" instead of promising a re-verification.
    const confirmText =
      reactivateKind === 'operator_off_sale'
        ? `Yeniden etkinleştir: ${account.accountHandle}. Parent operatör tarafından satıştan çekilmişti. `
          + (providerId === 'mailcom'
            ? MAILCOM_REACTIVATE_EFFECT
            : 'Hesap aktif olur, oturumu yeniden doğrulanır ve aliasları yeniden satışa girebilir '
              + '(GMX’te IMAP sağlık kapısı geçerli).')
          + ' Devam?'
        : reactivateKind === 'imap_rejected'
          ? `Yeniden etkinleştir: ${account.accountHandle}. IMAP girişi reddedildiği için devre dışı; `
            + (providerId === 'mailcom'
              ? 'yalnızca erişim düzeldiyse devam edin. ' + MAILCOM_REACTIVATE_EFFECT
              : 'yalnızca IMAP erişimi düzeldiyse devam edin. Hesap aktif olur, oturumu yeniden doğrulanır ve '
                + 'sistem IMAP girişini yeniden dener; IMAP girişi başarılı olana kadar sağlık kapısı onu satışa '
                + 'açmaz.')
            + ' Devam?'
          : `Yeniden etkinleştir: ${account.accountHandle}. Hesap telefon doğrulaması nedeniyle devre dışı; `
            + 'yalnızca telefon doğrulandıysa devam edin. Hesap aktif olur ve satıştan çekilen aliasları geri açılır. Devam?';
    if (!window.confirm(confirmText)) {
      return;
    }
    const reason = window.prompt("Neden? (≥4 karakter, audit log'a yazılır)");
    if (!reason || reason.trim().length < 4) {
      window.alert('Neden en az 4 karakter olmalı. İptal.');
      return;
    }
    const r = await reactivate.run({ reason: reason.trim() });
    if (r.kind === 'ok') onAfterAction();
  }

  const errorText =
    sessionRefresh.state.kind === 'error'
      ? `Oturum yenileme başarısız: ${sessionRefresh.state.reason}`
      : renewNow.state.kind === 'error'
        ? `Yenileme tetikleme başarısız: ${renewNow.state.reason}`
        : forceRenew.state.kind === 'error'
          ? `Force Renew başarısız: ${forceRenew.state.reason}`
          : clearCooldown.state.kind === 'error'
            ? `Cooldown sıfırlama başarısız: ${clearCooldown.state.reason}`
            : reactivate.state.kind === 'error'
              ? `Yeniden etkinleştirme başarısız: ${reactivate.state.reason}`
              : null;

  return {
    renewBlocked,
    phoneWall,
    reactivateKind,
    canTakeOffSale,
    canReactivate,
    sessionRefreshLoading: sessionRefresh.state.kind === 'running',
    renewNowLoading: renewNow.state.kind === 'running',
    forceRenewLoading: forceRenew.state.kind === 'running',
    clearCooldownLoading: clearCooldown.state.kind === 'running',
    reactivateLoading: reactivate.state.kind === 'running',
    cooldownActive,
    clearCooldownSupported: providerId === 'mailcom',
    sessionRefreshSupported: providerId === 'mailcom',
    anyError: errorText !== null,
    errorText,
    onRefreshSession,
    onRenewNow,
    onForceRenew,
    onClearCooldown,
    onReactivate,
  };
}

/* ─── per-row settings menu (⚙ → dropdown) ─────────────────────── */

interface RowActionsMenuProps {
  readonly account: WireProviderAccount;
  readonly canManage: boolean;
  readonly actions: RowActions;
  readonly onOpenAliases: (id: string) => void;
  readonly onOpenRenewals: (id: string) => void;
  readonly onOpenCredential: (id: string) => void;
  /** 2026-09-23 (D-239) — opens the row's "Satıştan çek" confirm dialog. */
  readonly onOpenTakeOffSale: () => void;
}

/** Why Force Renew is greyed on a reactivate-only disabled row. */
const FORCE_RENEW_BLOCKED_TITLE: Record<ReactivateKind, string> = {
  phone_wall: 'Telefon doğrulaması nedeniyle devre dışı. Önce Yeniden etkinleştir.',
  operator_off_sale: 'Operatör satıştan çekti. Önce Yeniden etkinleştir.',
  imap_rejected: 'IMAP girişi reddedildi. Önce Yeniden etkinleştir.',
};

/** Tooltip of the "Yeniden etkinleştir" item per kind. */
const REACTIVATE_TITLE: Record<ReactivateKind, string> = {
  phone_wall: 'Telefon doğrulandıysa hesabı aktif yapar ve satıştan çekilen aliasları geri açar.',
  operator_off_sale: 'Hesabı aktif yapar ve oturumunu yeniden doğrulatır; aliaslara dokunulmamıştı.',
  imap_rejected: 'IMAP erişimi düzeldiyse hesabı aktif yapar ve oturumunu yeniden doğrulatır.',
};

/**
 * mail.com variant (the rows where "Oturum yenile" exists): reactivation leaves
 * session_state 'expired' and nothing re-verifies it automatically.
 */
const REACTIVATE_TITLE_MAILCOM: Record<ReactivateKind, string> = {
  ...REACTIVATE_TITLE,
  operator_off_sale:
    'Hesabı aktif yapar; kalan aliasları hemen satışa döner (aliaslara dokunulmamıştı). '
    + 'Oturum kendiliğinden doğrulanmaz; gerekirse “Oturum yenile”.',
  imap_rejected:
    'Erişim düzeldiyse hesabı aktif yapar; kalan aliasları hemen satışa döner. '
    + 'Oturum kendiliğinden doğrulanmaz; gerekirse “Oturum yenile”.',
};

function RowActionsMenu({
  account,
  canManage,
  actions,
  onOpenAliases,
  onOpenRenewals,
  onOpenCredential,
  onOpenTakeOffSale,
}: RowActionsMenuProps): JSX.Element {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ readonly top: number; readonly left: number; readonly hidden: boolean } | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // Presentation only: the menu is viewport-positioned (position: fixed) next
  // to the ⚙ trigger, so the table's sideways scroller or a card body never
  // clips it, and it opens upward when there is no room below (last rows /
  // last card on a phone). It follows the trigger while anything scrolls.
  useLayoutEffect(() => {
    if (!menuOpen) return;
    const place = (): void => {
      const anchor = menuRef.current;
      const list = listRef.current;
      if (!anchor || !list) return;
      const r = anchor.getBoundingClientRect();
      const mh = list.offsetHeight;
      const mw = list.offsetWidth;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const GAP = 4;
      const EDGE = 8;
      // The sticky top bar is the upper edge: on a short screen (844×390) a
      // menu that fits neither below nor above its ⚙ used to be pinned at 8px,
      // over the top bar. Its height is capped below the bar (it scrolls
      // inside itself when taller).
      const headerBottom = document.querySelector('.adm-header')?.getBoundingClientRect().bottom ?? 0;
      const topEdge = Math.max(EDGE, headerBottom + EDGE);
      let top = r.bottom + GAP;
      if (top + mh > vh - EDGE) {
        const above = r.top - GAP - mh;
        top = above >= topEdge ? above : Math.max(topEdge, vh - EDGE - mh);
      }
      const left = Math.min(Math.max(EDGE, r.right - mw), Math.max(EDGE, vw - EDGE - mw));
      // Touch scrolling does not close the menu (no outside mousedown): while
      // its ⚙ is scrolled under the top bar or below the screen the menu is
      // not painted (it comes back with the trigger), so it never hangs over
      // the top bar without a visible trigger.
      const hidden = r.height > 0 && (r.bottom <= headerBottom || r.top >= vh);
      setMenuPos((p) => (p && p.top === top && p.left === left && p.hidden === hidden ? p : { top, left, hidden }));
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [menuOpen]);

  return (
    <div ref={menuRef} className="mcp-row-actions" style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        variant="ghost"
        size="sm"
        className="mcp-gear"
        onClick={() => setMenuOpen((o) => !o)}
        title="İşlemler"
        icon={<Icon.Settings size={14} />}
        data-testid={`row-actions-${account.accountHandle}`}
      />
      {menuOpen ? (
        <div
          ref={listRef}
          role="menu"
          className="mcp-row-menu"
          style={{
            position: 'fixed',
            top: menuPos?.top ?? 0,
            left: menuPos?.left ?? 0,
            maxHeight: 'calc(100dvh - var(--admin-header-h, 56px) - 16px)',
            overflowY: 'auto',
            // Under the sticky top bar (z 30), above the floating bulk bar (z 25).
            zIndex: 29,
            visibility: menuPos?.hidden ? 'hidden' : undefined,
            minWidth: 200,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <MenuItem
            onClick={() => {
              setMenuOpen(false);
              onOpenAliases(account.id);
            }}
            icon={<Icon.Mail size={13} />}
            label="Alias'lar"
            testId={`row-action-alias-${account.accountHandle}`}
          />
          {canManage ? (
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                void actions.onRenewNow();
              }}
              loading={actions.renewNowLoading}
              disabled={actions.renewBlocked}
              icon={<Icon.Refresh size={13} />}
              label={
                actions.renewBlocked
                  ? `Yenile (durum: ${ACCOUNT_STATUS_LABEL[account.status] ?? account.status})`
                  : 'Yenile'
              }
              testId={`row-action-renew-${account.accountHandle}`}
            />
          ) : null}
          <MenuItem
            onClick={() => {
              setMenuOpen(false);
              onOpenRenewals(account.id);
            }}
            icon={<Icon.Clock size={13} />}
            label="Yenileme & oturum geçmişi"
            testId={`row-action-history-${account.accountHandle}`}
          />
          {canManage ? (
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                onOpenCredential(account.id);
              }}
              icon={<Icon.Key size={13} />}
              label={account.hasCredential ? 'Credential güncelle' : 'Credential ekle'}
              testId={`row-action-credential-${account.accountHandle}`}
            />
          ) : null}
          {canManage && actions.sessionRefreshSupported ? (
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                void actions.onRefreshSession();
              }}
              loading={actions.sessionRefreshLoading}
              icon={<Icon.Sparkle size={13} />}
              label="Oturum yenile"
              testId={`row-action-session-${account.accountHandle}`}
            />
          ) : null}
          {canManage ? (
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                void actions.onForceRenew();
              }}
              loading={actions.forceRenewLoading}
              disabled={actions.reactivateKind !== null}
              title={
                actions.reactivateKind !== null
                  ? FORCE_RENEW_BLOCKED_TITLE[actions.reactivateKind]
                  : undefined
              }
              icon={<Icon.Bolt size={13} />}
              label="Force Renew"
              testId={`row-force-renew-${account.accountHandle}`}
            />
          ) : null}
          {canManage && actions.canTakeOffSale ? (
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                onOpenTakeOffSale();
              }}
              title="Parent'ı devre dışı yapar; aliaslara dokunulmaz, aktif siparişi varsa sunucu reddeder."
              icon={<Icon.Cancel size={13} />}
              label="Satıştan çek"
              testId={`row-take-off-sale-${account.accountHandle}`}
            />
          ) : null}
          {canManage && actions.canReactivate && actions.reactivateKind !== null ? (
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                void actions.onReactivate();
              }}
              loading={actions.reactivateLoading}
              title={(actions.sessionRefreshSupported ? REACTIVATE_TITLE_MAILCOM : REACTIVATE_TITLE)[actions.reactivateKind]}
              icon={<Icon.CheckCircle size={13} />}
              label="Yeniden etkinleştir"
              testId={`row-reactivate-${account.accountHandle}`}
            />
          ) : null}
          {/* D-239: the mail.com clear-cooldown flips the row to active, so it is
              not offered on a reactivate-only disabled row. */}
          {canManage && actions.cooldownActive && actions.clearCooldownSupported && actions.reactivateKind === null ? (
            <MenuItem
              onClick={() => {
                setMenuOpen(false);
                void actions.onClearCooldown();
              }}
              loading={actions.clearCooldownLoading}
              icon={<Icon.Refresh size={13} />}
              label="Cooldown Sıfırla"
              testId={`row-clear-cooldown-${account.accountHandle}`}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ─── desktop row ──────────────────────────────────────────────── */

function ParentMailRow({
  providerId,
  account,
  canManage,
  onOpenAliases,
  onOpenRenewals,
  onOpenCredential,
  onAfterAction,
  selection,
}: {
  readonly providerId: string;
  readonly account: WireProviderAccount;
  readonly canManage: boolean;
  readonly onOpenAliases: (id: string) => void;
  readonly onOpenRenewals: (id: string) => void;
  readonly onOpenCredential: (id: string) => void;
  readonly onAfterAction: () => void;
  readonly selection?: RowSelection;
}): JSX.Element {
  const actions = useRowActions(providerId, account, onAfterAction);
  const [offSaleOpen, setOffSaleOpen] = useState(false);

  return (
    <>
      <TRow data-testid={`mailcom-row-${account.accountHandle}`}>
        {selection ? (
          <TD style={{ width: 28 }}>
            <SelectBox selection={selection} handle={account.accountHandle} />
          </TD>
        ) : null}
        <TD title={`ID: ${account.id}`} className="mcp-td-email">
          <div className="t-mono" style={{ fontSize: 12.5, fontWeight: 500 }}>
            <HandleText handle={account.accountHandle} />
          </div>
        </TD>
        <TD>
          <StatusBadgePair
            status={account.status}
            statusDetail={account.statusDetail}
            suspendedReason={account.suspendedReason}
            failureReason={account.failureReason}
            handle={account.accountHandle}
          />
        </TD>
        <TD className="mcp-td-cooldown">
          {account.cooldownUntil && account.cooldownRemainingSec !== null && account.cooldownRemainingSec !== undefined ? (
            <CooldownChip
              cooldownUntil={account.cooldownUntil}
              initialRemainingSec={account.cooldownRemainingSec}
              suspendedReason={account.suspendedReason}
            />
          ) : (
            <span className="t-tertiary t-xs">—</span>
          )}
        </TD>
        <TD style={{ textAlign: 'center' }}>
          {account.hasCredential ? <Icon.Check size={14} /> : <Icon.X size={14} />}
        </TD>
        <TD>
          <PasswordCell providerId={providerId} account={account} canManage={canManage} />
        </TD>
        <TD
          style={{
            textAlign: 'right',
            fontWeight: account.consecutiveErrors > 0 ? 600 : 400,
            color: account.consecutiveErrors > 0 ? 'var(--danger-600)' : undefined,
          }}
        >
          {account.consecutiveErrors}
        </TD>
        <TD
          className="t-mono mcp-td-activity"
          style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}
          title={`Son fetch: ${fmtDate(account.lastSuccessfulFetchAt)}\nSon oturum: ${fmtDate(account.lastSessionCheckedAt)}`}
        >
          {fmtCompact(newerOf(account.lastSuccessfulFetchAt, account.lastSessionCheckedAt))}
        </TD>
        <TD style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{fmtDateOnly(account.createdAt)}</TD>
        <TD className="mcp-td-actions" style={{ textAlign: 'right' }}>
          <RowActionsMenu
            account={account}
            canManage={canManage}
            actions={actions}
            onOpenAliases={onOpenAliases}
            onOpenRenewals={onOpenRenewals}
            onOpenCredential={onOpenCredential}
            onOpenTakeOffSale={() => setOffSaleOpen(true)}
          />
          {/* Portalled to <body> by the dialog itself (no <tbody> nesting, no
              inherited cell styles). */}
          <TakeOffSaleDialog
            providerId={providerId}
            open={offSaleOpen}
            mode="single"
            targets={[account]}
            onClose={() => setOffSaleOpen(false)}
            onDone={() => onAfterAction()}
          />
        </TD>
      </TRow>
      {actions.anyError && actions.errorText ? (
        <TRow>
          {/* colSpan 9 — Email, Durum, Cooldown, Cred, Şifre, Hata, Son aktivite, Kayıt, İşlemler
              (+1 for the selection checkbox column when it is shown). */}
          <TD colSpan={selection ? 10 : 9} style={{ padding: '4px 12px', borderTop: 'none' }}>
            <div className="t-xs" style={{ color: 'var(--danger-600)' }}>
              {actions.errorText}
            </div>
          </TD>
        </TRow>
      ) : null}
    </>
  );
}

/* ─── menu item button ─────────────────────────────────────────── */

function MenuItem({
  onClick,
  loading,
  disabled,
  title,
  testId,
  icon,
  label,
}: {
  readonly onClick: () => void;
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly title?: string;
  readonly testId?: string;
  readonly icon: JSX.Element;
  readonly label: string;
}): JSX.Element {
  const isDisabled = Boolean(loading) || Boolean(disabled);
  return (
    <button
      type="button"
      role="menuitem"
      className="mcp-menu-item"
      onClick={onClick}
      disabled={isDisabled}
      title={title}
      data-testid={testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '7px 10px',
        background: 'transparent',
        border: 'none',
        borderRadius: 6,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        color: isDisabled ? 'var(--text-tertiary)' : 'var(--text-primary)',
        opacity: isDisabled ? 0.6 : 1,
        fontSize: 13,
      }}
    >
      {icon}
      {label}
    </button>
  );
}

/* ─── password reveal cell (click-to-reveal) ───────────────────── */

/**
 * 2026-06-06 — masked-by-default password with a per-row "göster" toggle.
 * Clicking calls POST .../credentials/reveal which DECRYPTS the stored
 * mail.com credential server-side (vault, AES-256-GCM) and returns the
 * password ONLY in that response (audited as an ACCESS, never the value).
 * "gizle" re-masks (drops the value from component state). Shown only when the
 * caller can manage AND the account actually has a stored credential.
 */
function PasswordCell({
  providerId,
  account,
  canManage,
}: {
  readonly providerId: string;
  readonly account: WireProviderAccount;
  readonly canManage: boolean;
}): JSX.Element {
  const reveal = useApiMutation((o, accountId: string) => revealMailcomCredential(o, accountId, providerId));
  const [shown, setShown] = useState<string | null>(null);

  if (!canManage || !account.hasCredential) {
    return <span className="t-tertiary t-xs">—</span>;
  }

  async function onToggle(): Promise<void> {
    if (shown !== null) {
      setShown(null); // re-mask
      return;
    }
    const r = await reveal.run(account.id);
    if (r.kind === 'ok') setShown(r.data.password);
  }

  const loading = reveal.state.kind === 'running';
  const errored = reveal.state.kind === 'error';

  return (
    <div className="mcp-pw" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        className="t-mono mcp-pw-value"
        style={{ fontSize: 12, userSelect: shown !== null ? 'all' : 'none', letterSpacing: shown !== null ? 0 : 1 }}
        data-testid={`pw-value-${account.accountHandle}`}
      >
        {shown !== null ? shown : '••••••••'}
      </span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => void onToggle()}
        loading={loading}
        title={shown !== null ? 'Şifreyi gizle' : 'Şifreyi göster (audit log\'a kaydedilir)'}
        data-testid={`pw-toggle-${account.accountHandle}`}
        icon={shown !== null ? <Icon.X size={13} /> : <Icon.Key size={13} />}
      >
        {shown !== null ? 'gizle' : 'göster'}
      </Button>
      {errored ? (
        <span className="t-xs mcp-pw-err" style={{ color: 'var(--danger-600)' }} title={reveal.state.kind === 'error' ? reveal.state.reason : ''}>
          hata
        </span>
      ) : null}
    </div>
  );
}

/* ─── mobile card ──────────────────────────────────────────────── */

/**
 * Mobile fallback (≤768px). 2026-05-28 operatör isteğiyle: oturum badge'i
 * kaldırıldı (V1'de yok), aksiyon butonları tek ⚙ menüsünde toplandı.
 */
function ParentMailMobileCard({
  providerId,
  account,
  canManage,
  onOpenAliases,
  onOpenRenewals,
  onOpenCredential,
  onAfterAction,
  selection,
}: {
  readonly providerId: string;
  readonly account: WireProviderAccount;
  readonly canManage: boolean;
  readonly onOpenAliases: (id: string) => void;
  readonly onOpenRenewals: (id: string) => void;
  readonly onOpenCredential: (id: string) => void;
  readonly onAfterAction: () => void;
  readonly selection?: RowSelection;
}): JSX.Element {
  const actions = useRowActions(providerId, account, onAfterAction);
  const [offSaleOpen, setOffSaleOpen] = useState(false);

  // Layout: line 1 = [select] handle (full width, wraps) [⚙]; line 2 = the
  // status badges flowing side by side (+ cooldown chip); then the
  // login-failure reason, the meta line and the password row.
  return (
    <div className="mcp-card" data-testid={`mailcom-mobile-card-${account.accountHandle}`}>
      <div className="mcp-card-head">
        {selection ? (
          <label className="mcp-card-check">
            <SelectBox selection={selection} handle={account.accountHandle} />
          </label>
        ) : null}
        <div className="t-mono mcp-card-handle">
          <HandleText handle={account.accountHandle} />
        </div>
        <RowActionsMenu
          account={account}
          canManage={canManage}
          actions={actions}
          onOpenAliases={onOpenAliases}
          onOpenRenewals={onOpenRenewals}
          onOpenCredential={onOpenCredential}
          onOpenTakeOffSale={() => setOffSaleOpen(true)}
        />
      </div>
      {/* Touch screens: why the checkbox is greyed out (its tooltip). */}
      {selection?.disabled ? <span className="touch-detail mcp-card-selnote">Devre dışı parent seçilemez</span> : null}
      <div className="mcp-card-badges">
        {/* The login-failure reason gets its own full-width line below on phones. */}
        <StatusBadgePair
          status={account.status}
          statusDetail={account.statusDetail}
          suspendedReason={account.suspendedReason}
          handle={account.accountHandle}
          layout="row"
        />
        {account.cooldownUntil && account.cooldownRemainingSec !== null && account.cooldownRemainingSec !== undefined ? (
          <CooldownChip
            cooldownUntil={account.cooldownUntil}
            initialRemainingSec={account.cooldownRemainingSec}
            suspendedReason={account.suspendedReason}
          />
        ) : null}
      </div>
      <TakeOffSaleDialog
        providerId={providerId}
        open={offSaleOpen}
        mode="single"
        targets={[account]}
        onClose={() => setOffSaleOpen(false)}
        onDone={() => onAfterAction()}
      />
      {account.status === 'login_failed' && account.failureReason ? (
        <div className="mcp-card-reason">
          <FailureReasonBadge status={account.status} reason={account.failureReason} handle={account.accountHandle} detail />
        </div>
      ) : null}
      <div className="mcp-card-meta">
        <span>
          Hata:{' '}
          <strong style={{ color: account.consecutiveErrors > 0 ? 'var(--danger-600)' : undefined }}>
            {account.consecutiveErrors}
          </strong>
        </span>
        <span>Cred: {account.hasCredential ? '✓' : '✗'}</span>
        <span className="mcp-card-created">Kayıt: {fmtDateOnly(account.createdAt)}</span>
        <span
          className="mcp-card-activity"
          title={`Son fetch: ${fmtDate(account.lastSuccessfulFetchAt)}\nSon oturum: ${fmtDate(account.lastSessionCheckedAt)}`}
        >
          Son aktivite:{' '}
          <span className="t-mono">{fmtCompact(newerOf(account.lastSuccessfulFetchAt, account.lastSessionCheckedAt))}</span>
          {/* Touch screens only: the two timestamps behind the tooltip. */}
          <span className="touch-detail">
            Son fetch: {fmtDate(account.lastSuccessfulFetchAt)} · Son oturum: {fmtDate(account.lastSessionCheckedAt)}
          </span>
        </span>
      </div>
      <div className="mcp-card-pw">
        <span>Şifre:</span>
        <PasswordCell providerId={providerId} account={account} canManage={canManage} />
      </div>
      {actions.anyError && actions.errorText ? (
        <div className="t-xs" style={{ color: 'var(--danger-600)' }}>
          {actions.errorText}
        </div>
      ) : null}
    </div>
  );
}
